import React from 'react'
import TextField from '@material-ui/core/TextField'
import { AutocompleteWrapper, TranslationDictionary } from '@plandi/common'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import Typography from '@material-ui/core/Typography'
import { components } from '@plandi/common/src/generated'

import { useProjectsFiltersStyles } from './projects-filters.styles'
import { useQueryProjectCategories } from '../project-queries'
import {
  useProjectsFilters,
  useSelectedCategoriesByFilter,
  useSelectedSubCategoriesByFilter
} from '../use-projects-filters'

const MAX_SUB_CATEGORIES = 15

type Props = {
  translate?: TranslationDictionary['data']
}

export const ProjectsFiltersCategories: React.FC<Props> = ({ translate }) => {
  const classes = useProjectsFiltersStyles()

  const { categoryIds, setProjectsFilters } = useProjectsFilters()

  const { data: categories } = useQueryProjectCategories()

  const { selectedCategories } = useSelectedCategoriesByFilter()

  const { subCategories, selectedSubCategories } =
    useSelectedSubCategoriesByFilter(undefined)

  const handleChangeCategories = (
    _: React.ChangeEvent<unknown>,
    categories: components['schemas']['ProjectMarketCategoryResource'][]
  ) => {
    const categoryIds = categories.map((category) => category.id) as number[]

    setProjectsFilters({
      categoryIds: categories.length ? categoryIds : undefined,
      subCategoryIds: undefined,
      tag: undefined
    })
  }

  const handleChangeSubCategories = (
    _: React.ChangeEvent<unknown>,
    subCategories: (
      | components['schemas']['ProjectMarketSubCategoryResource']
      | undefined
    )[]
  ) => {
    const subCategoryIds = subCategories.map(
      (subCategory) => subCategory?.id
    ) as number[]

    setProjectsFilters({
      subCategoryIds: subCategories.length ? subCategoryIds : undefined,
      tag: undefined
    })
  }

  return (
    <Accordion
      className={classes.accordion}
      classes={{ expanded: classes.accordionExpanded }}
      defaultExpanded
    >
      <AccordionSummary
        className={classes.accordionSummary}
        classes={{
          expanded: classes.accordionSummaryExpanded,
          content: classes.accordionSummaryContent
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography style={{ fontWeight: 500 }} variant="subtitle1">
          {translate?.shop['projects-page']['categories']}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div className={classes.details}>
          <AutocompleteWrapper
            translate={translate}
            multiple
            freeSolo={false}
            options={categories ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label={translate?.shop['projects-page']['category']}
                margin="normal"
                className={classes.categoriesInput}
              />
            )}
            getOptionLabel={(option) => option.name ?? ''}
            getOptionSelected={(option, value) => option.id === value.id}
            value={selectedCategories ?? []}
            onChange={handleChangeCategories}
          />
          <AutocompleteWrapper
            translate={translate}
            multiple
            freeSolo={false}
            options={subCategories ?? []}
            disabled={!categoryIds?.length}
            getOptionDisabled={() =>
              selectedSubCategories.length >= MAX_SUB_CATEGORIES
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label={translate?.shop['projects-page']['subcategory']}
                className={classes.categoriesInput}
                margin="normal"
              />
            )}
            getOptionLabel={(option) => option?.name ?? ''}
            value={selectedSubCategories ?? []}
            onChange={handleChangeSubCategories}
          />
        </div>
      </AccordionDetails>
    </Accordion>
  )
}
